import { isDefined } from "@clipboard-health/util-ts";
import { Button } from "@src/appV2/redesign/components/Button";
import { DocumentStatusPill } from "@src/appV2/redesign/Documents/components/StatusPill";
import { WORKPLACE_DOCUMENTS_FULL_PATH } from "@src/appV2/redesign/Documents/paths";
import { SHIFT_DISCOVERY_SHIFT_HELLO_SIGN_CONTAINER_MODAL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useShiftModalsDataContext";
import type { IllustrationType } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import pluralize from "pluralize";
import { generatePath, useHistory } from "react-router-dom";

// eslint-disable-next-line unicorn/prevent-abbreviations
import { usePandaDocConfig } from "../../../Documents/Details/Workflows/PandaDoc/usePandaDocConfig";
// eslint-disable-next-line unicorn/prevent-abbreviations
import { usePandaDocSigningEnabled } from "../../../Documents/Details/Workflows/PandaDoc/usePandaDocSigningEnabled";
import { getIncompleteRequirementPrerequisiteActions } from "../../../Documents/utils/getIncompleteRequirementPrerequisiteActions";
import { QuickSignPill } from "../../Bookability/Documents/InstantReview/QuickSignPill";
import { type BlockingRequirementsContext } from "../../Bookability/Documents/types";
import { ShiftBottomSheetBookabilityCriteriaCard } from "../BookabilityCriteriaCard";

interface ShiftBottomSheetGroupedBlockingRequirementsCardProps {
  blockingRequirementsContext: BlockingRequirementsContext;
  shiftId: string;
  qualification?: string;
  workplaceId: string;
}

export function ShiftBottomSheetGroupedBlockingRequirementsCard(
  props: ShiftBottomSheetGroupedBlockingRequirementsCardProps
) {
  const { blockingRequirementsContext, shiftId, qualification, workplaceId } = props;
  const { navigateToModal } = useShiftModalsDataContext();
  const history = useHistory();
  const worker = useDefinedWorker();
  const {
    areAllVisibleBlockedDocumentsInstantReview,
    visibleBlockingRequirements,
    areAllVisibleBlockedDocumentsInReview,
    notInReviewVisibleBlockingRequirements,
  } = blockingRequirementsContext;

  // eslint-disable-next-line unicorn/prevent-abbreviations
  const isPandaDocSigningEnabled = usePandaDocSigningEnabled(
    visibleBlockingRequirements[0]?.requirementId ?? ""
  );
  // eslint-disable-next-line unicorn/prevent-abbreviations
  const { redirectToSupport, useFallbackUrl } = usePandaDocConfig();

  function getPill() {
    if (areAllVisibleBlockedDocumentsInReview) {
      return <DocumentStatusPill variant="in_review" />;
    }

    if (areAllVisibleBlockedDocumentsInstantReview) {
      return <QuickSignPill />;
    }

    return null;
  }

  function getIcon(): IllustrationType {
    if (areAllVisibleBlockedDocumentsInReview) {
      return "document-review";
    }

    if (areAllVisibleBlockedDocumentsInstantReview) {
      return "quiz-required";
    }

    return "document-upload";
  }

  function getTitle() {
    if (areAllVisibleBlockedDocumentsInReview) {
      const numberOfDocumentsInReview = visibleBlockingRequirements.length;
      return `The ${numberOfDocumentsInReview} ${pluralize(
        "document",
        numberOfDocumentsInReview
      )} you've submitted are being reviewed by our team`;
    }

    const count = notInReviewVisibleBlockingRequirements.length;
    return `Complete ${count} additional ${pluralize("document", count)} to book this shift`;
  }

  function onClick() {
    const [firstVisibleBlockingRequirement] = visibleBlockingRequirements;
    const isPrerequisiteActionRequired = firstVisibleBlockingRequirement
      ? getIncompleteRequirementPrerequisiteActions({
          worker,
          workerRequirement: firstVisibleBlockingRequirement,
        }).length > 0
      : false;

    const needFallbackUI = isPandaDocSigningEnabled && (redirectToSupport || useFallbackUrl);
    if (
      !isPrerequisiteActionRequired &&
      !needFallbackUI &&
      areAllVisibleBlockedDocumentsInstantReview
    ) {
      navigateToModal(SHIFT_DISCOVERY_SHIFT_HELLO_SIGN_CONTAINER_MODAL_PATH, {
        pathParams: {
          requirementId: visibleBlockingRequirements[0].requirementId,
          shiftId,
        },
        forceReplacePath: true,
      });
      return;
    }

    const searchParams = new URLSearchParams();
    if (isDefined(qualification)) {
      searchParams.set("qualification", qualification);
    }

    searchParams.set("shiftId", shiftId);
    const path = generatePath(WORKPLACE_DOCUMENTS_FULL_PATH, {
      workplaceId,
    });
    history.push(`${path}?${searchParams.toString()}`);
  }

  return (
    <ShiftBottomSheetBookabilityCriteriaCard
      title={getTitle()}
      illustrationType={getIcon()}
      pill={getPill()}
      footer={
        <Button
          fullWidth
          variant={areAllVisibleBlockedDocumentsInReview ? "outlined" : "contained"}
          size="medium"
          onClick={() => {
            onClick();
          }}
        >
          Complete Documents
        </Button>
      }
    />
  );
}
